// src/app/core/auth/auth.service.ts
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { LoginService } from 'app/shared/services/edduca-imob/login/login.service';
import { UsuarioService } from 'app/shared/services/edduca-imob/usuario/usuario.service';
import { UserService } from 'app/core/user/user.service';
import { Router } from '@angular/router';
import { LoginRequest } from 'app/shared/services/edduca-imob/login/request/login.request';
import { LoginResponse } from 'app/shared/services/edduca-imob/login/response/login.response';
import { UsuarioResponse } from 'app/shared/services/edduca-imob/usuario/response/login.response';
import { SessionViewModel } from 'app/shared/models/ei-models';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated$ = new BehaviorSubject<boolean>(false);
  readonly authenticated$ = this._authenticated$.asObservable();

  // snapshot (usado pelo NoAuthGuard e LoginComponent)
  get isAuthenticatedSync(): boolean {
    return this._authenticated$.value && !!this.accessToken;
  }

  // token em localStorage
  get accessToken(): string { return localStorage.getItem('token') ?? ''; }
  private set accessToken(token: string) { localStorage.setItem('token', token); }

  private loginService   = inject(LoginService);
  private usuarioService = inject(UsuarioService);
  private userService    = inject(UserService);
  private router         = inject(Router);

  private _loginResponse: LoginResponse | null = null;
  get loginResponse(): LoginResponse | null { return this._loginResponse; }
  private set loginResponse(v: LoginResponse | null) { this._loginResponse = v; }

  private _checkInFlight?: Promise<boolean>; // evita chamadas concorrentes

  // async signIn(request: LoginRequest): Promise<LoginResponse> {
  //   if (this._authenticated$.value) throw new Error('Já autenticado');

  //   const loginResp = await this.loginService.login(request);
  //   this.accessToken = loginResp.token;
  //   localStorage.setItem('usuarioId', String(loginResp.usuarioId));
  //   this._authenticated$.next(true);

  //   const usr: UsuarioResponse = await firstValueFrom(
  //     this.usuarioService.getUsuarioById(loginResp.usuarioId)
  //   );
  //   this.userService.user = { id: String(usr.id), name: usr.nome, email: usr.email };
  //   this.loginResponse = loginResp;
  //   return loginResp;
  // }

  async check(): Promise<boolean> {
    if (this._checkInFlight) return this._checkInFlight;

    this._checkInFlight = (async () => {
      const token = this.getTokenOrNull();
      if (!token) { this.clearSession(); return false; }

      try {
        const valid = await firstValueFrom(
          this.loginService.validateToken().pipe(timeout({ each: 8000 }))
        );
        if (!valid) { this.clearSession(); return false; }

        this._authenticated$.next(true);

        const userId = Number(localStorage.getItem('usuarioId') || 0);
        if (!userId) { this.clearSession(); return false; }

        const usr = await firstValueFrom(this.usuarioService.getUsuarioById(userId));
        this.userService.user = { 
          id: String(userId), 
          name: usr.nome, 
          email: usr.email,
          termosAceitos: usr.termosAceitos 
        };
        this.loginResponse = { usuarioId: String(userId), token, email: usr.email } as any;

        return true;
      } catch {
        this.clearSession(); return false;
      } finally {
        this._checkInFlight = undefined;
      }
    })();

    return this._checkInFlight;
  }

  private getTokenOrNull(): string | null {
    const t = localStorage.getItem('token');
    if (!t || t === 'null' || t === 'undefined' || t.trim() === '') return null;
    return t;
  }

  private clearSession(): void {
    this._authenticated$.next(false);
    this.loginResponse = null;
    localStorage.removeItem('token');
    localStorage.removeItem('usuarioId');
    this.userService.user = null!;
  }

  signOut(): void {
    localStorage.clear();
    this._authenticated$.next(false);
    this.loginResponse = null;
    this.userService.user = null!;
    this.router.navigate(['/login']);
  }


  getToken(): string {
    return localStorage.getItem('token')!;
  }



  getTokenInfo() {
    try {
      const token = this.getToken();
      const helper = new JwtHelperService();
      const decodedToken = helper.decodeToken(token);
      return decodedToken;
    } catch (error) {
      return null;
    }

  }
}
